import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { formatCurrency } from "../matter-overview-utils";
import { ProviderCard } from "../components/ProviderCard";
import { SectionAction } from "../components/SectionAction";
import { SummaryCell } from "../components/SummaryCell";
import {
  sortTreatmentsByEarliestAppointment,
  useMedicalTreatmentViewModel,
} from "@/hooks/matter-view-models/use-medical-treatment-view-model";

export function TreatmentSummary({
  matter,
  title = "Treatment summary",
  embedded = false,
}: {
  matter: Matter;
  title?: string;
  embedded?: boolean;
}) {
  const totals = calculateMedicalTreatmentBillTotals(
    matter.medicalTreatmentBills,
  );
  return (
    <section
      className={cn(
        "bg-card min-w-0 rounded-2xl border p-5",
        embedded && "rounded-none border-0 p-0",
      )}
    >
      <h3 className="border-b pb-4 text-sm font-semibold break-words">
        {title}
      </h3>
      <div className="grid gap-5 pt-5 sm:grid-cols-3">
        <SummaryCell
          className="min-w-0 px-0 py-0"
          label="Total bills"
          value={formatCurrency(totals.totalBill)}
        />
        <SummaryCell
          className="min-w-0 px-0 py-0"
          label="Paid"
          value={formatCurrency(totals.totalPaid)}
        />
        <SummaryCell
          className="min-w-0 px-0 py-0"
          label="Outstanding"
          value={formatCurrency(totals.totalOutstanding)}
        />
      </div>
    </section>
  );
}

export function ProvidersSection({
  matter,
  title = "Treatments",
  embedded = false,
}: {
  matter: Matter;
  title?: string;
  embedded?: boolean;
}) {
  const { hasPagePermission } = useUser();
  const canViewTreatments = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailMedicalTreatments,
  );
  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const sortedTreatments = sortTreatmentsByEarliestAppointment(
    completeMedicalTreatments,
  );
  return (
    <section className="min-w-0">
      <div
        className={cn(
          "mb-4 flex flex-wrap items-center justify-between gap-3",
          embedded && "border-b pb-4",
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <h3
            className={cn(
              "text-foreground text-2xl font-bold break-words",
              embedded && "text-sm font-semibold",
            )}
          >
            {title}
          </h3>
          <span className="border-border/80 text-muted-foreground inline-flex h-7 min-w-7 items-center justify-center rounded-xl border px-2 text-sm">
            {sortedTreatments.length}
          </span>
        </div>
        {canViewTreatments ? (
          <SectionAction
            to="/matters/$matterId/medical-treatments"
            params={{ matterId: matter.id }}
            label="View all treatments"
          />
        ) : null}
      </div>

      <div className="space-y-4">
        {sortedTreatments.length > 0 ? (
          sortedTreatments.map((treatment) => (
            <ProviderCard
              key={treatment.id}
              matter={matter}
              treatment={treatment}
            />
          ))
        ) : (
          <div className="bg-card text-muted-foreground rounded-2xl border p-5 text-sm">
            No medical treatments added yet.
          </div>
        )}
      </div>
    </section>
  );
}
